import React, { useEffect, useState } from 'react';
import axios from '@/utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import * as XLSX from 'xlsx';
import { FaFileExport } from "react-icons/fa";
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

interface Stock {
  id: string;
  product: { name: string };
  warehouse: { name: string };
  location: {
    name: string;
    warehouse?: {
      name: string;
    };
  };
  qty: number;
  qtyAvailable: number;
  qtyReserved: number;
}

const StockList = () => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');


  const { logout } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}` || '',
      'Content-Type': 'application/json',
    },
  });

  const fetchStocks = async () => {
	  try {
		/*
		const res = await axios.get(`${API}/stocks`, {
		  ...getAuthHeader(),
		  params: {
			page: currentPage,
			limit: rowsPerPage,
			search,
			sort: sortField,
			order: sortOrder,
		  },
		});*/
		const params: any = {
		  page: currentPage,
		  limit: rowsPerPage,
		  search,
		};

		if (sortField) {       // hanya kirim sort jika ada field
		  params.sort = sortField;
		  params.order = sortOrder;
		}

		const res = await axios.get(`${API}/stocks`, { ...getAuthHeader(), params });
		setStocks(res.data.data || []);
		//console.log(res.data.data[0].location.warehouse.name);
		setTotal(res.data.total || 0);
	  } catch (err: any) {
		if (err.response?.status === 401) {
		  logout();
		  navigate('/login');
		}
	  }
	};
	
  const handleSort = (field: string) => {
	  if (sortField === field) {
		setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
	  } else {
		setSortField(field);
		setSortOrder('asc');
	  }
	};


  const handleExport = () => {
    const exportData = stocks.map((item) => ({
      'Warehouse': item.location?.name || '-',
	  'Product': item.product?.name || '-',
      'Location': item.location?.name || '-',
      'Qty': item.qty,
      'Available': item.qtyAvailable,
      'Reserved': item.qtyReserved,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock');
    XLSX.writeFile(workbook, 'StockList.xlsx');
  };
  

  useEffect(() => {
    fetchStocks();
  }, [currentPage, rowsPerPage, sortField, sortOrder]);
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchStocks();
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const maxPage = Math.ceil(total / rowsPerPage);

  return (
    <div className="p-1 mx-auto max-w-6xl">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Stock Location</h2>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              placeholder="Search..."
              className="border px-2 py-1 rounded text-[13px] w-full pr-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                &#10005;
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className="border rounded px-2 py-1 text-[13px]"
            >
              {[10, 25, 50, 100].map((num) => (
                <option key={num} value={num}>{num} / page</option>
              ))}
            </select>
            |
            <button
              onClick={handleExport}
              className="text-blue-600 hover:text-blue-800 text-lg"
              title="Export to Excel"
            >
              <FaFileExport />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border rounded mb-2">
          <table className="min-w-full text-[13px]">
            <thead className="bg-gray-100">
			  <tr>
				{['warehouse', 'product', 'location', 'qty', 'qtyAvailable', 'qtyReserved'].map((key) => {
				  const labelMap: Record<string, string> = {
					warehouse: 'Warehouse',
					product: 'Product',
					location: 'Location',
					qty: 'Qty',
					qtyAvailable: 'Available',
					qtyReserved: 'Reserved',
				  };
				  const label = labelMap[key];
				  const isSorted = sortField === key;
				  const arrow = isSorted ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';

				  return (
					<th
					  key={key}
					  className={`border px-2 py-2 text-center cursor-pointer select-none ${isSorted ? 'bg-blue-50 text-blue-700 font-semibold' : ''}`}
					  onClick={() => handleSort(key)}
					>
					  {label}{arrow}
					</th>
				  );
				})}
			  </tr>
			</thead>

            <tbody>
              {stocks.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
				  <td className="border px-2 py-1">{item.location?.warehouse?.name || '-'}</td>
                  <td className="border px-2 py-1">{item.product?.name || '-'}</td>
                  <td className="border px-2 py-1">{item.location?.name || '-'}</td>
                  <td className="border px-2 py-1 text-right">{item.qty}</td>
                  <td className="border px-2 py-1 text-right">{item.qtyAvailable}</td>
                  <td className="border px-2 py-1 text-right">{item.qtyReserved}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4 text-[13px] flex-wrap gap-2">
          <div>
            Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, total)} of {total}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 border rounded">
              <ChevronsLeft size={18} />
            </button>
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-2 py-1 border rounded">«</button>

            {Array.from({ length: maxPage }, (_, i) => i + 1)
              .filter((page) => Math.abs(page - currentPage) <= 2)
              .map((page) => (
                <button
                  key={page}
                  className={`px-2 py-1 border rounded ${page === currentPage ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

            <button onClick={() => setCurrentPage(p => Math.min(p + 1, maxPage))} disabled={currentPage === maxPage} className="px-2 py-1 border rounded">»</button>
            <button onClick={() => setCurrentPage(maxPage)} disabled={currentPage === maxPage} className="px-2 py-1 border rounded">
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockList;
